import React, { useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import { useDictionary } from '../../../../../utilities/hooks/UseDictionary';
import SimpleLoader from '../../../common/SimpleLoader';
import ExperienceStep from '../../new_experience/ExperienceStep';
import RecommendationNameAndDetails from './steps/RecommendationNameAndDetails';
import RecommendationLocation from './steps/RecommendationLocation';
import RecommendationPhotos from './steps/RecommendationPhotos';
import {
	getRecommendationById,
	createRecommendation,
	updateRecommendation,
	uploadRecommendationAsset,
	deleteRecommendationAsset
} from '../networking';

const TOTAL_STEPS = 3;

// Add/edit recommendation wizard. Mirrors the new-experience stepper layout.
// Save flow is create-then-upload: the recommendation is saved first, then any
// pending photos are uploaded to its id (assets need an existing recommendation).
const RecommendationDetails = () => {
	const dictionary = useDictionary();
	const history = useHistory();
	const { id } = useParams();

	const [step, setStep] = useState(1);
	const [max, setMax] = useState(id ? TOTAL_STEPS : 1);
	const [enabled, setEnabled] = useState(false);
	const [showLoader, setShowLoader] = useState(!!id);

	const [section, setSection] = useState('recommended_place');
	const [name, setName] = useState('');
	const [description, setDescription] = useState('');
	const [directions, setDirections] = useState('');
	const [place, setPlace] = useState(null);
	const [existingAssets, setExistingAssets] = useState([]);
	const [filesToUpload, setFilesToUpload] = useState([]);

	const initLabel = id
		? dictionary.edit_recommendation
		: dictionary.add_recommendation;

	useEffect(() => {
		if (id) {
			getRecommendationAsync();
		}
	}, []);

	useEffect(() => {
		if (step === 1) {
			setEnabled(name.trim() !== '' && section !== '');
		} else if (step === 2) {
			setEnabled(!!place?.placeId);
		} else {
			setEnabled(true);
		}
	}, [step, name, section, place]);

	const getRecommendationAsync = async () => {
		setShowLoader(true);
		try {
			const { data } = await getRecommendationById(id);
			setSection(data?.relation_type ?? 'recommended_place');
			setName(data?.name ?? '');
			setDescription(data?.description ?? '');
			setDirections(data?.directions ?? '');
			setExistingAssets(data?.assets ?? []);
			if (data?.point_of_interest) {
				const poi = data.point_of_interest;
				setPlace({
					name: poi.name,
					placeId: poi.place_id,
					location:
						poi.latitude != null && poi.longitude != null
							? { latitude: poi.latitude, longitude: poi.longitude }
							: null
				});
			}
		} catch (error) {
			Sentry.captureException(error);
		} finally {
			setShowLoader(false);
		}
	};

	const steps = {
		1: (
			<RecommendationNameAndDetails
				section={section}
				setSection={setSection}
				name={name}
				setName={setName}
				description={description}
				setDescription={setDescription}
			/>
		),
		2: (
			<RecommendationLocation
				place={place}
				setPlace={setPlace}
				directions={directions}
				setDirections={setDirections}
			/>
		),
		3: (
			<RecommendationPhotos
				existingAssets={existingAssets}
				filesToUpload={filesToUpload}
				setFilesToUpload={setFilesToUpload}
				onDeleteExistingAsset={onDeleteExistingAsset}
			/>
		)
	};

	function setIncrementalStep(index) {
		setStep(index);
		if (index > max) {
			setMax(index);
		}
	}

	const onNextClicked = () => {
		if (!enabled) return;
		if (step === TOTAL_STEPS) {
			saveRecommendation();
		} else {
			setIncrementalStep(step + 1);
		}
	};

	const onPreviousClicked = () => {
		setIncrementalStep(step - 1);
	};

	const onCancelClicked = () => {
		history.goBack();
	};

	async function onDeleteExistingAsset(asset) {
		if (!id) return;
		try {
			await deleteRecommendationAsset(id, asset.id);
			setExistingAssets((prev) => prev.filter((a) => a.id !== asset.id));
		} catch (error) {
			Sentry.captureException(error);
		}
	}

	const uploadPendingPhotos = async (recommendationId) => {
		for (const file of filesToUpload) {
			try {
				await uploadRecommendationAsset(recommendationId, file);
			} catch (error) {
				Sentry.captureException(error);
			}
		}
	};

	const saveRecommendation = async () => {
		if (name.trim() === '' || section === '') {
			setIncrementalStep(1);
			return;
		}
		if (!place?.placeId) {
			setIncrementalStep(2);
			return;
		}
		setShowLoader(true);
		try {
			let recommendationId = id;
			if (id) {
				await updateRecommendation(id, {
					name: name,
					relation_type: section,
					place_id: place?.placeId,
					description: description,
					directions: directions
				});
			} else {
				const { data } = await createRecommendation({
					name: name,
					relationType: section,
					placeId: place?.placeId,
					description: description,
					directions: directions
				});
				recommendationId = data?.id;
			}

			if (recommendationId && filesToUpload.length > 0) {
				await uploadPendingPhotos(recommendationId);
			}
			setShowLoader(false);
			history.goBack();
		} catch (error) {
			Sentry.captureException(error);
			setShowLoader(false);
		}
	};

	return (
		<div className="new-experience-wrapper">
			<div className="new-experience-nav-wrapper">
				<div
					className="round-close-button-white new-experience-close-button"
					onClick={onCancelClicked}
				/>
				<div className="new-experience-nav-container">
					<div className="new-experience-nav-top-container">
						<div className="new-experience-nav-top-logo" />
						<div onClick={saveRecommendation} className="sidebar-button">
							{dictionary.save_and_exit}
						</div>
					</div>
					<h2
						className="new-experience-nav-title mobile-invisible"
						style={{ color: 'white' }}
					>
						{initLabel}
					</h2>
					<div className="new-experience-nav-items-container thin-scrollbar">
						<ExperienceStep
							step={1}
							max={max}
							activeStep={step}
							name={dictionary.recommendation_step_details}
							setStep={setIncrementalStep}
						/>
						<ExperienceStep
							step={2}
							max={max}
							activeStep={step}
							name={dictionary.recommendation_step_location}
							setStep={setIncrementalStep}
						/>
						<ExperienceStep
							step={3}
							max={max}
							activeStep={step}
							name={dictionary.recommendation_step_photos}
							setStep={setIncrementalStep}
						/>
					</div>
				</div>
			</div>
			<div className="new-experience-content-wrapper">
				<div className="new-experience-content-container">{steps[step]}</div>
				<div className="new-experience-bottom-navigation-container">
					{step !== 1 ? (
						<div
							onClick={onPreviousClicked}
							className="button secondary-button"
						>
							{dictionary.back}
						</div>
					) : null}
					<div
						onClick={onNextClicked}
						className={enabled ? 'button' : 'button button-disabled'}
					>
						{step === TOTAL_STEPS ? dictionary.finish : dictionary.next}
					</div>
				</div>
			</div>

			<SimpleLoader showLoader={showLoader} />
		</div>
	);
};

export default RecommendationDetails;
